import { useState, useCallback } from "react";
import { mutate } from "swr";
import {
  usePosContext,
  POS_CUSTOMER_INFO_KEY,
} from "../context/pos-context";
import { getStorage, setStorage } from "@/hooks/use-local-storage";

const POS_CUSTOMERS_BY_TELEGRAM_KEY_PREFIX = "/pos/customers/by-telegram/";

export function useCustomerInfoCheckout() {
  const {
    handleCheckout,
    telegramId,
    telegramDisplayName,
    customerByTelegram,
    isCustomerByTelegramLoading,
  } = usePosContext();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const customerName =
    customerByTelegram?.name?.trim() || telegramDisplayName?.trim() || "";
  const customerPhone = customerByTelegram?.phone?.trim() ?? "";
  const hasCustomerFromTelegram =
    Boolean(telegramId) && !isCustomerByTelegramLoading && Boolean(customerByTelegram);
  const hasNameAndPhoneFromTelegram =
    hasCustomerFromTelegram && Boolean(customerName && customerPhone);
  const hasNameOnlyFromTelegram =
    Boolean(telegramId) && Boolean(customerName) && !customerPhone;

  const handlePayClick = useCallback(() => {
    const storedInfo = getStorage(POS_CUSTOMER_INFO_KEY) as
      | { customerName?: string; customerPhone?: string; customerAddress?: string }
      | null;
    const storedName = storedInfo?.customerName?.trim() ?? "";
    const storedPhone = storedInfo?.customerPhone?.trim() ?? "";
    const hasStoredInfo = Boolean(storedName && storedPhone);

    if (hasNameAndPhoneFromTelegram && customerByTelegram) {
      setStorage(POS_CUSTOMER_INFO_KEY, {
        customerName: customerName,
        customerPhone: customerPhone,
        customerAddress: customerByTelegram?.address ?? "",
      });
      handleCheckout({ customerId: customerByTelegram.id });
      return;
    }

    if (hasStoredInfo) {
      handleCheckout(
        customerByTelegram ? { customerId: customerByTelegram.id } : undefined
      );
      return;
    }

    setIsDialogOpen(true);
  }, [
    handleCheckout,
    hasNameAndPhoneFromTelegram,
    customerName,
    customerPhone,
    customerByTelegram,
  ]);

  const handleCustomerInfoSubmit = useCallback(
    async (name: string, phone: string, address?: string) => {
      const trimmedName = name.trim();
      const trimmedPhone = phone.trim();
      const trimmedAddress = address?.trim() ?? "";
      if (!trimmedName || !trimmedPhone) return;

      setStorage(POS_CUSTOMER_INFO_KEY, {
        customerName: trimmedName,
        customerPhone: trimmedPhone,
        customerAddress: trimmedAddress,
      });
      setIsDialogOpen(false);
      await handleCheckout(
        customerByTelegram ? { customerId: customerByTelegram.id } : undefined
      );
      if (telegramId) {
        mutate(`${POS_CUSTOMERS_BY_TELEGRAM_KEY_PREFIX}${telegramId}`);
      }
    },
    [handleCheckout, customerByTelegram, telegramId]
  );

  const dialogMode: "both" | "phoneOnly" =
    hasNameOnlyFromTelegram ? "phoneOnly" : "both";
  const dialogInitialName = customerName || undefined;
  const dialogInitialPhone = customerPhone || undefined;
  const dialogInitialAddress = customerByTelegram?.address?.trim() || undefined;

  return {
    isDialogOpen,
    setIsDialogOpen,
    handlePayClick,
    handleCustomerInfoSubmit,
    dialogMode,
    dialogInitialName,
    dialogInitialPhone,
    dialogInitialAddress,
  };
}
